import { apiAuthFetch } from "@/lib/authFetch";

/**
 * Snapshot returned by the anonymization PATCH endpoint and embedded in the thread
 * detail response. Mirrors the backend's persisted column 1:1 — `enabled` is a concrete
 * `true`/`false`, never null. The TS-side field is named `enabled` (not `anonymizationEnabled`)
 * so it lines up with `AnonymizationThreadState` in the Zustand store; the wire JSON
 * still carries `anonymizationEnabled` (mapped on read below).
 *
 *   - `enabled === true | false` → persisted per-thread setting.
 *   - `everAnonymized === true` → at least one prior turn successfully anonymized;
 *     drives the flip-off warning modal.
 */
export interface AnonymizationState {
  enabled: boolean;
  everAnonymized: boolean;
}

/**
 * Sets the per-thread anonymization toggle. Always `true` or `false` — there is no
 * "clear the override" semantic anymore. Call sites should gate the request on a
 * confirmation modal whenever the next state is `false` AND the current `everAnonymized`
 * flag is `true` — otherwise the user is about to send their history-so-far in clear to
 * the LLM without realising it.
 */
export async function setThreadAnonymization(
  threadId: string,
  enabled: boolean,
): Promise<AnonymizationState> {
  const response = await apiAuthFetch(
    `/api/threads/${encodeURIComponent(threadId)}/anonymization`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enabled }),
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to update anonymization toggle: ${response.status} ${response.statusText}`,
    );
  }

  // Wire field is `anonymizationEnabled`; map to the in-memory `enabled` shape so
  // the Zustand store update at the call site has the right key. Without this map
  // step the optimistic update (which writes `{ enabled, everAnonymized }`) was
  // being overwritten by `{ anonymizationEnabled, everAnonymized }` and the switch
  // visually bounced back because `state?.enabled` was undefined.
  const data = await response.json();
  return {
    enabled: data.anonymizationEnabled === true,
    everAnonymized: data.everAnonymized === true,
  };
}

/**
 * Wire error code emitted by the backend when the GPU anonymisation worker is
 * unreachable on a turn where the user had anonymisation enabled. Frontend should
 * listen for this on the stream and render a typed error modal.
 */
const ANONYMIZATION_UNAVAILABLE_ERROR_CODE = "anonymization_unavailable";
